import { Box, Button, Paper, Typography } from '@mui/material'
import ShippingsRecordingView from './View'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { ReactComponent as Boxes } from '../../assets/icons/boxes.svg'
import { useStyles } from './styles'
import ModalLayout from '../../ui/modals/layout'
import useTranslations from '../../i18n/useTranslations'
import { useEffect, useState } from 'react'
import ShippingsRecordingTable from './TableRecording/View'
import FileUploadModal from './FileUploadModal'
import SuccessfulRecordingModal from './SuccessfulRecordingModal'
import ChoosePickup from './FileUploadModal/choosePickup'
import {
  useDeleteFileMutation,
  useGetFileDetailMutation,
  useManifestFiles,
  useShippingsRecordingTracking,
} from '../../services/shippingsRecording'
import { useUserContext } from '../../auth/UserProvider'
import Loader from '../../ui/views/Loader'
import SearchInput from '../ShippingsTracking/SearchInput'
import { format } from 'date-fns'
import { ReactComponent as Upload } from '../../assets/icons/upload-white.svg'

const EmptyState = ({ onClickImport, emptySearch }) => {
  const classes = useStyles()
  const t = useTranslations()
  return (
    <Paper className={clsx(classes.paper, classes.emptyState)}>
      {emptySearch ? (
        <>
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('empty:no-results')}
          </Typography>
          <Typography>
            <span className={classes.secondaryText}>
              {t('empty:no-results-search')}
            </span>
          </Typography>
        </>
      ) : (
        <>
          <Boxes />
          <Typography variant="h1" className={clsx('decorator', classes.title)}>
            {t('shipping-recording:massive-request')}
          </Typography>
          <Typography>
            <span
              onClick={onClickImport}
              className={classes.primaryText}
              style={{ cursor: 'pointer' }}
            >
              {t('shipping-recording:import-your-file')}
            </span>
            <span className={classes.secondaryText}>
              {t('shipping-recording:save-time')}
            </span>
          </Typography>
        </>
      )}
    </Paper>
  )
}

const ShippingsRecording = () => {
  const t = useTranslations()
  const classes = useStyles()
  const [uploadFileModal, setUploadFileModal] = useState(false)
  const [showPickUpModal, setShowPickUpModal] = useState(false)
  const [centerSelected, setCenterSelected] = useState(null)
  const [extensionAndName, setExtensionandName] = useState({
    extension: 'pdf',
    name: 'file',
  })
  const [manifestStatus, setManifestStatus] = useState()
  const [showAlert, setShowAlert] = useState()
  const [filesToUpload, setFilesToUpload] = useState([])
  const [intervalID, setIntervalID] = useState()
  const [isLoadingRefetch, setIsLoadingRefetch] = useState(false)
  const [tableParams, setTableParams] = useState({
    search: '',
    page: 0,
    rowsPerPage: 10,
    totalRows: 28,
    sortType: 'desc',
    sortColumn: 'upload_datetime',
    dateFrom: format(
      new Date(Date.now()).setMonth(new Date().getMonth() - 3),
      'yyyy-MM-dd',
    ),
    dateTo: format(
      new Date(Date.now()).setDate(new Date().getDate() + 1),
      'yyyy-MM-dd',
    ),
  })

  const { clientId, centres } = useUserContext()

  const manifestFilesMutation = useManifestFiles(
    clientId,
    () => {
      setManifestStatus('success')
    },
    () => setManifestStatus('error'),
  )

  const resetVariables = () => {
    setUploadFileModal(false)
    setShowPickUpModal(false)
    setManifestStatus()
    setShowAlert(null)
    setFilesToUpload([])
    setIsLoadingRefetch(false)
  }

  const hasPickUp = () => {
    for (let i = 0; i < centres.length; i++) {
      const address = centres[i]
      if (
        address?.fixedPickupsAdresses?.some(direction =>
          direction.timeZones.some(zona => zona.length > 0),
        )
      ) {
        return true
      }
    }
    return false
  }

  const handleAddFile = document => {
    setFilesToUpload([...filesToUpload, ...document])
  }

  const handleRemoveFile = id => {
    const newFiles = [...filesToUpload]
    const index = newFiles.findIndex(element => element.id === id)
    filesToUpload.splice(index, 1)
    setFilesToUpload([...filesToUpload])
  }

  const {
    isLoading: isLoadingFiles,
    data: filesData,
    refetch: refetchTracking,
    setData: setFilesData,
  } = useShippingsRecordingTracking({
    client_code: clientId,
    filters: {
      dateFrom: tableParams.dateFrom,
      dateTo: tableParams.dateTo,
      page: tableParams.page,
      rowsPerPage: tableParams.rowsPerPage,
      sortType: tableParams.sortType,
      sortColumn: tableParams.sortColumn,
      search: tableParams.search,
    },
  })

  const refetchFiles = async noLoading => {
    try {
      if (!noLoading) setIsLoadingRefetch(true)
      const newData = await refetchTracking({
        client_code: clientId,
        filters: {
          dateFrom: tableParams.dateFrom,
          dateTo: tableParams.dateTo,
          page: tableParams.page,
          rowsPerPage: tableParams.rowsPerPage,
          sortType: tableParams.sortType,
          sortColumn: tableParams.sortColumn,
          search: tableParams.search,
        },
      })
      if (setFilesData) setFilesData(newData?.data)
    } catch (error) {
      console.error('Error fetching data:', error)
    }
    if (!noLoading) setIsLoadingRefetch(false)
  }

  const deleteFileMutation = useDeleteFileMutation(
    () => {
      refetchFiles()
      setShowAlert({
        severity: 'success',
        text: 'shipping-recording:delete-file-success',
      })
    },
    () =>
      setShowAlert({
        severity: 'error',
        text: 'shipping-recording:delete-file-error',
      }),
  )

  const handleSubmitDelete = id => {
    deleteFileMutation.mutate({
      clientCode: clientId,
      id: id,
    })
  }

  const downloadCSV = async data => {
    try {
      const binaryData = atob(data?.data)
      const arrayBuffer = new ArrayBuffer(binaryData.length)
      const uint8Array = new Uint8Array(arrayBuffer)

      for (let i = 0; i < binaryData.length; i++) {
        uint8Array[i] = binaryData.charCodeAt(i)
      }

      const blob = new Blob([uint8Array], {
        type: `application/${extensionAndName?.extension}`,
      })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${extensionAndName?.name}.${extensionAndName?.extension}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const getFileDetail = useGetFileDetailMutation(downloadCSV)

  const handleSubmitGetDetail = async (id, provider, extension, name) => {
    await setExtensionandName({ extension: extension, name: `${name}_errors` })
    getFileDetail.mutate({
      clientCode: clientId,
      id: id,
      provider: provider,
    })
  }

  useEffect(() => {
    let timer
    if (showAlert !== null) {
      timer = setTimeout(() => {
        setShowAlert(null)
      }, 5000)
    }

    return () => clearTimeout(timer)
  }, [showAlert])

  useEffect(() => {
    const fetchData = async () => {
      await refetchFiles()
    }

    fetchData()
  }, [
    tableParams.rowsPerPage,
    tableParams.search,
    clientId,
    refetchTracking,
    setFilesData,
    tableParams.dateFrom,
    tableParams.dateTo,
    tableParams.page,
    tableParams.sortColumn,
    tableParams.sortType,
  ])

  useEffect(() => {
    const fetchData = async () => {
      await refetchFiles()
    }
    if (manifestStatus === 'success') fetchData()
  }, [manifestStatus])

  useEffect(() => {
    let intervalId
    let pendingOrProcessing = []
    if (filesData?.data?.length) {
      filesData?.data?.forEach(item => {
        if (
          item.status === 'PENDING' ||
          item.status === 'PROCESSING' ||
          (item.status === 'ERRORED' && !item?.error_file?.resource_provider)
        ) {
          pendingOrProcessing.push(item)
        }
      })
    }
    if (!intervalID && pendingOrProcessing?.length) {
      intervalId = setInterval(async () => {
        await refetchFiles(true)
      }, 10000)
      setIntervalID(intervalId)
    } else if (!pendingOrProcessing?.length) {
      clearInterval(intervalID)
      setIntervalID(null)
    }
  }, [filesData])

  useEffect(() => {
    setTableParams({
      ...tableParams,
      totalRows: filesData?.pagination?.record_count,
    })
  }, [filesData])

  const renderModal = () => {
    return !manifestStatus ? (
      <ModalLayout
        onClose={() => {
          setShowPickUpModal(false)
          setFilesToUpload([])
          setUploadFileModal(false)
        }}
        isOpen={uploadFileModal}
        wide={true}
        content={
          <Box className={classes.modalContainer}>
            {!showPickUpModal ? (
              <FileUploadModal
                handleRemoveFile={handleRemoveFile}
                handleAddFile={handleAddFile}
                openModal={setUploadFileModal}
                filesToUpload={filesToUpload}
                hasPickUp={hasPickUp()}
                setShowPickUpModal={setShowPickUpModal}
                manifestFiles={manifestFilesMutation}
                centerSelected={centerSelected}
                setCenterSelected={setCenterSelected}
              />
            ) : (
              <ChoosePickup
                onClose={() => {
                  setShowPickUpModal(false)
                  setFilesToUpload([])
                  setUploadFileModal(false)
                }}
                filesToUpload={filesToUpload}
                hasPickUp={
                  centerSelected &&
                  centerSelected.fixedPickupsAdresses?.some(direction =>
                    direction.timeZones.some(zona => zona.length > 0),
                  )
                    ? centerSelected
                    : null
                }
                manifestFiles={manifestFilesMutation}
                centerSelected={centerSelected}
              />
            )}
          </Box>
        }
      />
    ) : (
      <SuccessfulRecordingModal
        hasPickUp={hasPickUp()}
        isOpen={manifestStatus === 'success'}
        onClose={resetVariables}
      />
    )
  }

  return (
    <>
      <ShippingsRecordingView showAlert={showAlert}>
        {renderModal()}
        <Box className={classes.row}>
          {filesData?.data?.length || tableParams.search?.length ? (
            <SearchInput
              className={classes.searchInput}
              queryValue={tableParams.search}
              noHelperText={true}
              placeholder={t('home:shipment-recording-input-placeholder')}
              search={value => {
                setTableParams({ ...tableParams, search: value })
              }}
              clearSearch={() => {
                setTableParams({ ...tableParams, search: undefined })
              }}
            />
          ) : null}
          <Button
            onClick={() => setUploadFileModal(true)}
            size={'small'}
            startIcon={<Upload />}
            className={classes.uploadButton}
          >
            {t('action:import-file')}
          </Button>
        </Box>
        {isLoadingFiles || isLoadingRefetch || !filesData?.data ? (
          <Loader />
        ) : filesData?.data?.length ? (
          <ShippingsRecordingTable
            rows={filesData?.data ?? []}
            handleSubmitDelete={handleSubmitDelete}
            handleSubmitGetDetail={handleSubmitGetDetail}
            tableParams={tableParams}
            onChangeTableParams={setTableParams}
          />
        ) : (
          <EmptyState
            emptySearch={tableParams.search?.length}
            onClickImport={() => setUploadFileModal(true)}
          />
        )}
      </ShippingsRecordingView>
    </>
  )
}

ShippingsRecording.propTypes = {}
EmptyState.propTypes = {
  onClickImport: PropTypes.func,
  emptySearch: PropTypes.bool,
}

export default ShippingsRecording
